import React, { createContext, useContext, useRef, ReactNode } from "react";
import { SustainabilityMetricEmitter } from "../../../../sdk/metrics";

interface MetricEmitterContextType {
  metricEmitter: SustainabilityMetricEmitter;
}

const MetricEmitterContext = createContext<MetricEmitterContextType | null>(
  null,
);

interface MetricEmitterProviderProps {
  children: ReactNode;
  value?: SustainabilityMetricEmitter;
}

/**
 * Provides a single shared metric emitter instance to all child components.
 * Can accept an existing emitter instance via the `value` prop for cross-shadow-DOM sharing.
 */
export const MetricEmitterProvider: React.FC<MetricEmitterProviderProps> = ({
  children,
  value,
}) => {
  const metricEmitterRef = useRef<SustainabilityMetricEmitter | null>(null);

  if (!metricEmitterRef.current) {
    metricEmitterRef.current = value || new SustainabilityMetricEmitter();
  }

  return (
    <MetricEmitterContext.Provider
      value={{ metricEmitter: metricEmitterRef.current }}
    >
      {children}
    </MetricEmitterContext.Provider>
  );
};

export const useMetricEmitter = (): SustainabilityMetricEmitter => {
  const context = useContext(MetricEmitterContext);

  if (!context) {
    throw new Error(
      "useMetricEmitter must be used within a MetricEmitterProvider",
    );
  }

  return context.metricEmitter;
};
